import { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { ContinueEvent } from './continueModel';
import { LessonEnd } from './LessonEnd';
import { PlayerControls } from './PlayerControls';
import { PlayerStage } from './PlayerStage';
import { StepList } from './StepList';
import { UpNext } from './UpNext';
import { WalkthroughPlayer } from './WalkthroughPlayer';
import { markDone, markSeen, resumeIndex } from './progressModel';
import { progressStore } from './useLessonProgress';
import { useLessonPlayer, type LessonPlayer } from './useLessonPlayer';
import { StepNarration, useContinuePhase, usePlayerKeys, ViewerHeader, type ViewerProps } from './ViewerParts';
import { VideoLessonViewer } from './VideoLessonViewer';

type Mode = 'steps' | 'walkthrough';


function ModeSwitch({ mode, onMode }: { mode: Mode; onMode: (m: Mode) => void }) {
  return (
    <ToggleGroup type="single" value={mode} onValueChange={(v) => v && onMode(v as Mode)} aria-label="Lesson format" spacing={1} className="grid w-full grid-cols-2 rounded-lg border bg-secondary p-1 sm:w-fit">
      <ToggleGroupItem value="steps" data-testid="lesson-mode-steps" className="h-9 w-full max-sm:h-11">
        Steps
      </ToggleGroupItem>
      <ToggleGroupItem value="walkthrough" data-testid="lesson-mode-walkthrough" className="h-9 w-full max-sm:h-11">
        Walkthrough video
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

/** The player's actions as the viewer means them: each one also tells the continue phase what the viewer did. */
function useViewerActions(player: LessonPlayer, send: (event: ContinueEvent) => void) {
  return {
    toggle: () => {
      send(player.finished ? 'replay' : player.playing ? 'pause' : 'play');
      player.toggle();
    },
    goTo: (index: number) => {
      if (player.finished) send('replay');
      player.goTo(index);
    },
    pause: () => {
      send('pause');
      player.pause();
    },
  };
}

/**
 * The lesson player (port of the Portal player's behavior): step screenshot with captions, narration audio per step,
 * previous/next, step dots, auto-advance when a step's audio ends, keyboard, the walkthrough video when the lesson has
 * one, and progress remembered per lesson. "Up next" names the following lesson all along; when the lesson ends, a
 * countdown opens it (it plays), unless the viewer paused, canceled or replayed.
 */
function ScreenshotLessonViewer({ lesson, nextLesson, progress, autoPlay, onClose, onOpenLesson }: ViewerProps) {
  const [mode, setMode] = useState<Mode>('steps');
  const [captions, setCaptions] = useState(true);
  const { phase, send } = useContinuePhase(nextLesson !== null);
  const player = useLessonPlayer(lesson, {
    initialIndex: resumeIndex(progressStore.read(), lesson),
    autoPlay,
    onStep: (i) => progressStore.update((m) => markSeen(m, lesson, i)),
    onFinished: () => {
      progressStore.update((m) => markDone(m, lesson));
      send('finished');
    },
  });
  const act = useViewerActions(player, send);
  usePlayerKeys((key) => {
    if (key === 'close') return onClose();
    if (mode !== 'steps') return;
    if (key === 'toggle') act.toggle();
    else act.goTo(player.index + (key === 'next' ? 1 : -1));
  });
  const switchMode = (next: Mode) => {
    act.pause();
    setMode(next);
  };

  return (
    <section data-testid="lesson-viewer" data-lesson={lesson.id} data-continue={phase} className="flex min-w-0 flex-col gap-5">
      <ViewerHeader lesson={lesson} onClose={onClose} />
      {/* Phone: stage, Up next, narration, steps. Desktop: stage and narration left; Up next above the steps right. */}
      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:grid-rows-[auto_1fr] lg:items-start xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-3 max-lg:order-1 lg:col-start-1 lg:row-start-1">
          {lesson.walkthrough ? <ModeSwitch mode={mode} onMode={switchMode} /> : null}
          {mode === 'walkthrough' && lesson.walkthrough ? (
            <WalkthroughPlayer video={lesson.walkthrough.video} audio={lesson.walkthrough.audio} title={lesson.title} />
          ) : (
            <>
              <PlayerStage step={player.step} playing={player.playing} progress={player.progress} finished={player.finished} blocked={player.blocked} captions={captions} onToggle={act.toggle}>
                <LessonEnd nextLesson={nextLesson} counting={phase === 'counting'} onReplay={act.toggle} onCancel={() => send('cancel')} onNextLesson={onOpenLesson} onClose={onClose} />
              </PlayerStage>
              <PlayerControls steps={lesson.steps} index={player.index} playing={player.playing} progress={player.progress} captions={captions} onCaptions={setCaptions} onToggle={act.toggle} onGoTo={act.goTo} />
            </>
          )}
        </div>
        {mode === 'steps' ? (
          <div className="min-w-0 max-lg:order-3 lg:col-start-1 lg:row-start-2">
            <StepNarration step={player.step} index={player.index} />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-col gap-5 max-lg:contents lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <UpNext next={nextLesson} onOpen={onOpenLesson} onClose={onClose} className="max-lg:order-2" />
          {mode === 'steps' ? (
            <div className="min-w-0 max-lg:order-4">
              <StepList steps={lesson.steps} index={player.index} seen={progress[lesson.id]?.seen ?? []} onGoTo={act.goTo} />
            </div>
          ) : null}
        </div>
      </div>
      <audio ref={player.audioRef} preload="auto" hidden data-testid="lesson-audio" {...player.audioEvents} />
    </section>
  );
}

/** A video lesson (its steps are chapters of one recorded video) plays in VideoLessonViewer; every other in the steps player. */
export function LessonViewer(props: ViewerProps) {
  return props.lesson.chapters ? <VideoLessonViewer {...props} chapters={props.lesson.chapters} /> : <ScreenshotLessonViewer {...props} />;
}
